import { BadgeCheck, Clock } from "lucide-react";
import { useVerification } from "@/hooks/useVerification";
import { useBusinessConfig } from "@/lib/use-business-config";

interface Props {
  /** "dark" for use on dark surfaces (footer); "light" otherwise. */
  tone?: "dark" | "light";
  /** Compact pill (default) vs. inline label. */
  variant?: "pill" | "inline";
  /** Show a "Verification pending" pill when not yet verified. Defaults to false. */
  showPending?: boolean;
  className?: string;
}

/**
 * Renders a trust badge:
 *   - "Verified address" when an admin has attested AND the snapshot postcode
 *     still matches the live business postcode.
 *   - Optionally "Verification pending" when not verified (showPending=true).
 *
 * The postcode guard prevents a stale "Verified" claim if the address changes
 * later without re-attestation.
 */
export function VerifiedAddressBadge({
  tone = "light",
  variant = "pill",
  showPending = false,
  className = "",
}: Props) {
  const v = useVerification();
  const biz = useBusinessConfig();

  const livePostcode = (biz.postcode || "").replace(/\s+/g, "").toUpperCase();
  const verifiedPostcode = (v.postalCode || "").replace(/\s+/g, "").toUpperCase();
  const matches = !!livePostcode && livePostcode === verifiedPostcode;
  const isVerified = v.verified && matches;

  if (!isVerified) {
    if (!showPending) return null;
    const pendingLabel = "Address verification pending";
    if (variant === "inline") {
      return (
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone === "dark" ? "text-amber-300" : "text-amber-700"} ${className}`}
          title={pendingLabel}
        >
          <Clock className="h-4 w-4" aria-hidden="true" />
          <span>{pendingLabel}</span>
        </span>
      );
    }
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${
          tone === "dark"
            ? "border-amber-400/30 bg-amber-400/10 text-amber-200"
            : "border-amber-600/30 bg-amber-50 text-amber-800"
        } ${className}`}
        title={pendingLabel}
        aria-label={pendingLabel}
      >
        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
        <span>Verification pending</span>
      </span>
    );
  }

  const date = v.verifiedAt
    ? new Date(v.verifiedAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : "";
  const label = `Address verified${date ? ` · ${date}` : ""}${v.hoursVerified ? " · Hours verified" : ""}`;

  if (variant === "inline") {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone === "dark" ? "text-emerald-300" : "text-emerald-700"} ${className}`}
        title={label}
      >
        <BadgeCheck className="h-4 w-4" aria-hidden="true" />
        <span>{label}</span>
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${
        tone === "dark"
          ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"
          : "border-emerald-600/30 bg-emerald-50 text-emerald-800"
      } ${className}`}
      title={label}
      aria-label={label}
    >
      <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
      <span>Verified address</span>
    </span>
  );
}
